
// Path: src/components/referral/ReferralP.tsx
// Enhanced mobile responsiveness with stacked layouts, touch-friendly buttons, and ARIA roles
// Optimized InlineShareButtons for mobile with fewer networks and smaller buttons
// Ensured smooth scrolling with touch-action: pan-y
// Maintained SEO and OLS Sun Light Meditation theme
// 06/07/25 10:15 AM +07

import React from 'react';
import { Breadcrumb, Button, Layout, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { InlineShareButtons } from 'sharethis-reactjs';
import './ReferralP.css';
import ReferralScenarioCalculator from './ReferralScenarioCalculator';

const { Title, Text } = Typography;

const StyledContainer = styled.div`
  max-width: 100%;
  overflow-x: hidden;
  padding: 0 10px;
`;

const ShareThisWrapper = styled.div`
  margin-top: 30px;
  text-align: center;
`;

const ReferralList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 20px 0;
`;

const ReferralItem = styled.li`
  margin-bottom: 20px;
  padding: 15px;
  background: #2a2a2a;
  border: 2px solid #ffaa00;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 15px;
`;

const ReferralLevel = styled.span`
  font-size: 24px;
  font-weight: bold;
  color: #ffcc00;
`;

const ReferralIcon = styled.span`
  font-size: 32px;
`;

const ReferralP: React.FC = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "OLS Referral Program - Become an OLS Promoter",
    description: "Join the OLS Referral Program as an OLS Promoter and earn commissions by sharing Sun Light Meditation education. Discover unlimited referral opportunities at olsme.com/OLSStudentProspect!",
    url: "https://www.olsme.com/referral",
    publisher: {
      "@type": "Organization",
      name: "onelightsystem OLS",
      url: "https://www.olsme.com",
      logo: "https://www.olsme.com/logo.jpg",
    },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "OLS iee.aeo",
          item: "https://www.olsme.com/OLSaeo",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Referral Program",
          item: "https://www.olsme.com/referral",
        },
      ],
    },
  };

  return (
    <Layout className="referral-layout" role="main">
      <Helmet>
        <title>OLS Referral Program - Become an OLS Promoter</title>
        <meta
          name="description"
          content="Join the OLS Referral Program as an OLS Promoter and earn commissions by sharing Sun Light Meditation education. Discover unlimited referral opportunities at olsme.com/OLSStudentProspect!"
        />
        <meta
          name="keywords"
          content="OLS Referral Program, Sun Light Meditation, OLS Promoter, join OLS, referral commissions, meditation education, Sun Light Civilization"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="onelightsystem OLS" />
        <link rel="canonical" href="https://www.olsme.com/referral" />
        <meta property="og:title" content="OLS Referral Program - Become an OLS Promoter" />
        <meta
          property="og:description"
          content="Join the OLS Referral Program as an OLS Promoter and earn commissions by sharing Sun Light Meditation education. Discover unlimited referral opportunities at olsme.com/OLSStudentProspect!"
        />
        <meta property="og:url" content="https://www.olsme.com/referral" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://via.placeholder.com/1200x630?text=OLS+Referral+Program" />
        <meta property="og:image:alt" content="OLS Referral Program Announcement" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="onelightsystem OLS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@onelightsystem" />
        <meta name="twitter:title" content="OLS Referral Program - Become an OLS Promoter" />
        <meta
          name="twitter:description"
          content="Join the OLS Referral Program as an OLS Promoter and earn commissions by sharing Sun Light Meditation education. Discover unlimited referral opportunities at olsme.com/OLSStudentProspect!"
        />
        <meta name="twitter:image" content="https://via.placeholder.com/1200x630?text=OLS+Referral+Program" />
        <meta name="twitter:image:alt" content="OLS Referral Program Announcement" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>
      <section className="referral-content-section" aria-label="Referral Program Content">
        <StyledContainer>
          <div className="referral-content">
            <Breadcrumb className="referral-breadcrumb">
              <Breadcrumb.Item>
                <Link to="/OLSaeo">OLS iee.aeo</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>Referral Program</Breadcrumb.Item>
            </Breadcrumb>

            <Title level={2} className="referral-title">
              Become an OLS Promoter: Join the Referral Program
            </Title>

            <div className="definitions-section" role="region" aria-label="What is an OLS Promoter">
              <Title level={3} className="section-title">What is an OLS Promoter?</Title>
              <Text className="description-text">
                An <strong>OLS Promoter</strong> is an active OLS user who participates in the Referral Program to share the transformative power of Sun Light Meditation. By becoming a promoter, you gain access to the OLS Referral Program, where you can earn commissions by referring new users to join OLS. Each successful referral not only helps grow the Sun Light Civilization but also rewards you with commissions across multiple levels.
              </Text>
            </div>

            <ReferralScenarioCalculator />

            <div className="commission-section" role="region" aria-label="Referral Commission Structure">
              <Title level={3} className="section-title">Referral Commission Structure</Title>
              <Text className="description-text">
                As an OLS Promoter, you can earn commissions on unlimited Levels, we will show an Example
                of the 5 levels of referrals:
                <ReferralList>
                  <ReferralItem>
                    <ReferralIcon>👤</ReferralIcon>
                    <strong><ReferralLevel>1st Level:</ReferralLevel></strong> Earn 5% for each new user you refer who joins OLS.
                  </ReferralItem>
                  <ReferralItem>
                    <ReferralIcon>👥</ReferralIcon>
                    <strong><ReferralLevel>2nd Level:</ReferralLevel></strong> Earn 4% when your referrals refer others.
                  </ReferralItem>
                  <ReferralItem>
                    <ReferralIcon>👥👥</ReferralIcon>
                    <strong><ReferralLevel>3rd Level:</ReferralLevel></strong> Earn 3% on 3rd-level referrals.
                  </ReferralItem>
                  <ReferralItem>
                    <ReferralIcon>👥👥👥</ReferralIcon>
                    <strong><ReferralLevel>4th Level:</ReferralLevel></strong> Earn 2% on 4th-level referrals.
                  </ReferralItem>
                  <ReferralItem>
                    <ReferralIcon>👥👥👥👥</ReferralIcon>
                    <strong><ReferralLevel>5th Level:</ReferralLevel></strong> Earn 1% on 5th-level referrals.
                  </ReferralItem>
                </ReferralList>
                <strong>Potential Earnings Example:</strong><br />
                Refer 10 users paying $100 each: You earn $50 (5% of $1,000).<br />
                Those 10 refer 5 each (50 users, $5,000 total): You earn $200 (4% of $5,000).<br />
                Those 50 refer 3 each (150 users, $15,000 total): You earn $450 (3% of $15,000).<br />
                Those 150 refer 2 each (300 users, $30,000 total): You earn $600 (2% of $30,000).<br />
                Those 300 refer 1 each (300 users, $30,000 total): You earn $300 (1% of $30,000).<br />
                <strong>Total Potential Earnings:</strong> $1,600 from one referral chain!
              </Text>
            </div>

            <div className="diagram-section" role="region" aria-label="How Referrals Work">
              <Title level={3} className="section-title">How Referrals Work</Title>
              <div className="referral-diagram">
                <div className="referral-level">
                  <span className="referral-icon">👤</span>
                  <Text className="diagram-text">You (OLS Promoter): Unlimited Referrals → 5% Commission</Text>
                </div>
                <div className="referral-arrow">↓</div>
                <div className="referral-level">
                  <span className="referral-icon">👥</span>
                  <Text className="diagram-text">Your Referrals → 4% Commission (2nd Level)</Text>
                </div>
                <div className="referral-arrow">↓</div>
                <div className="referral-level">
                  <span className="referral-icon">👥👥</span>
                  <Text className="diagram-text">Their Referrals → 3% Commission (3rd Level)</Text>
                </div>
                <div className="referral-arrow">↓</div>
                <div className="referral-level">
                  <span className="referral-icon">👥👥👥</span>
                  <Text className="diagram-text">4th Level → 2% Commission</Text>
                </div>
                <div className="referral-arrow">↓</div>
                <div className="referral-level">
                  <span className="referral-icon">👥👥👥👥</span>
                  <Text className="diagram-text">5th Level → 1% Commission</Text>
                </div>
              </div>
            </div>

            <div className="promoter-section" role="region" aria-label="Become an OLS Promoter">
              <Title level={3} className="section-title">Become an OLS Promoter</Title>
              <Text className="description-text">
                Ready to start earning commissions and grow the Sun Light Civilization? Join as an OLS Promoter today! As a promoter, you’ll receive:
                <ul>
                  <li>Access to a unique referral link to share with your network.</li>
                  <li>Support from OLS organizers to maximize your referrals.</li>
                  <li>Potential for additional organizer bonuses based on referral performance.</li>
                </ul>
              </Text>
              <Button type="primary" className="cta-button">
                <Link to="/register" aria-label="Join OLS as a promoter">
                  Join OLS Now
                </Link>
              </Button>
              <Text className="description-text">
                Already an OLS member? Access your referral link at <Link to="/home/referral">/home/referral</Link> to start sharing!
              </Text>
            </div>

            <div className="social-share-section" role="region" aria-label="Share the Referral Program">
              <Title level={3} className="section-title">Share the Referral Program</Title>
              <ShareThisWrapper>
                <InlineShareButtons
                  config={{
                    alignment: "center",
                    color: "social",
                    enabled: true,
                    font_size: 12,
                    labels: "cta",
                    language: "en",
                    networks: ["whatsapp", "facebook", "twitter", "linkedin", "copy_link"],
                    padding: 10,
                    radius: 3,
                    size: 24,
                    show_count: false,
                    show_total: true,
                    min_count: 0,
                    url: undefined,
                    image: undefined,
                    description: "Join the OLS Referral Program as an affiliate to share Sun Light Meditation and earn commissions! 🌞",
                    title: "OLS Referral Program",
                    message: "Become an OLS affiliate and promote Sun Light Meditation!",
                    subject: "Join OLS Referral Program",
                    username: "onelightsystem",
                  }}
                />
              </ShareThisWrapper>
            </div>
          </div>
        </StyledContainer>
      </section>
    </Layout>
  );
};

export default ReferralP;